import { useCallback, useEffect, useState } from 'react'
import { bettingApi } from '../../api'
import { ErrorState, errorMessage } from '../ErrorState'
import type { BettingService, BettingTestAccount, JourneyDepth, PaymentTestRow } from '../../types'
import { useShell } from '../../shell/ShellContext'
import { currentQuery, navigate, useRoute, withQuery } from '../../shell/router'
import { Block, PageHeader, SidePanel, StatusPill } from '../../ui/ui'
import { useI18n, useT } from '../../i18n'
import type { Formatters, MessageKey, TFunction } from '../../i18n'
import {
  depthLabel,
  FixtureBadge,
  FixtureToggle,
  RunStatusBadge,
  runStatusLabel,
  severityLabel,
  SeverityBadge,
  countryLabel,
  formatDateTime,
  useBettingVocabulary,
} from './bettingShared'

/** Fréquences proposées ; les autres valeurs retombent sur « tous les N jours ». */
const FREQUENCY_KEYS: Record<number, MessageKey> = {
  0: 'bet.freqManual',
  1: 'bet.freqDaily',
  7: 'bet.freqWeekly',
  14: 'bet.freqFortnightly',
  30: 'bet.freqMonthly',
}

function frequencyLabel(t: TFunction, days: number): string {
  const key = FREQUENCY_KEYS[days]
  return key ? t(key) : t('bet.freqEveryNDays', { n: days })
}

function nextCheck(t: TFunction, fmt: Formatters, service: BettingService): string | null {
  if (!service.checkFrequencyDays || !service.isActive) return null
  if (!service.lastRunAt) return t('bet.nextPass')
  const next = new Date(service.lastRunAt)
  next.setDate(next.getDate() + service.checkFrequencyDays)
  return next < new Date() ? t('bet.nextPass') : fmt.date(next)
}

export function BettingServices({ canManage }: { canManage: boolean }) {
  useRoute() // relit le service sélectionné dans l'ancre
  const { t, fmt } = useI18n()
  const { country, showFixtures, setShowFixtures } = useShell()
  const vocab = useBettingVocabulary()
  const [services, setServices] = useState<BettingService[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<BettingService | 'new' | null>(null)
  const [accountsFor, setAccountsFor] = useState<BettingService | null>(null)
  const selectedId = currentQuery().get('service')

  const load = useCallback(() => {
    setError(null)
    bettingApi
      .listServices(country || undefined, showFixtures)
      .then(setServices)
      .catch((err) => setError(errorMessage(err)))
  }, [country, showFixtures])
  useEffect(load, [load])

  const select = (id: string | null) => navigate(withQuery('betting/services', { service: id }))
  const selected = services?.find((s) => s.id === selectedId) ?? null

  const active = services?.filter((s) => s.isActive) ?? []
  const withAccount = active.filter((s) => s.accountCount).length
  const monthAgo = Date.now() - 30 * 86_400_000
  const stale = active.filter((s) => !s.lastTest || new Date(s.lastTest.createdAt).getTime() < monthAgo).length

  return (
    <>
      <PageHeader
        title={t('bet.servicesTitle')}
        description={
          services
            ? t('bet.servicesCount', { n: active.length, accounts: withAccount, stale })
            : t('bet.servicesDesc')
        }
        actions={
          <>
            <FixtureToggle checked={showFixtures} onChange={setShowFixtures} />
            {canManage && (
              <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
                {t('bet.addService')}
              </button>
            )}
          </>
        }
      />

      <Block flush>
        {error ? (
          <ErrorState message={error} onRetry={load} />
        ) : !services ? (
          <div className="stat-skeleton" style={{ minHeight: 160, margin: 16 }} />
        ) : services.length === 0 ? (
          <div className="table-empty">
            {country ? t('bet.noServiceInCountry') : t('bet.noService')}
          </div>
        ) : (
          <div className="table-scroll">
            <table className="bet-table bet-table-clickable">
              <thead>
                <tr>
                  <th scope="col">{t('bet.colService')}</th>
                  <th scope="col">{t('common.country')}</th>
                  <th scope="col">{t('bet.colAccess')}</th>
                  <th scope="col">{t('bet.colControl')}</th>
                  <th scope="col">{t('bet.colLastTest')}</th>
                </tr>
              </thead>
              <tbody>
                {services.map((s) => (
                  <tr
                    key={s.id}
                    tabIndex={0}
                    className={`${s.isActive ? '' : 'bet-row-inactive'}${s.id === selectedId ? ' bet-row-selected' : ''}`}
                    onClick={() => select(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') select(s.id)
                    }}
                  >
                    <td>
                      <div className="bet-service-cell">
                        <strong>{s.name}<FixtureBadge when={s.isFixture} /></strong>
                        <span>{s.operatorCompany ?? t('bet.companyUnknown')}{s.isActive ? '' : t('bet.disabledSuffix')}</span>
                      </div>
                    </td>
                    <td>{countryLabel(vocab, s.country, t)}</td>
                    <td>{s.accountCount ? t('bet.testAccount') : <span className="bet-muted">{t('bet.publicPages')}</span>}</td>
                    <td>{frequencyLabel(t, s.checkFrequencyDays)}</td>
                    <td>
                      {s.lastTest ? (
                        <span className="bet-last-test">
                          <SeverityBadge severity={s.lastTest.severity} />
                          {s.lastTest.runStatus !== 'COMPLETED' && <RunStatusBadge status={s.lastTest.runStatus} />}
                          <span className="num bet-muted">{fmt.date(s.lastTest.createdAt)}</span>
                        </span>
                      ) : (
                        <span className="bet-muted">{t('bet.neverTested')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Block>

      {selected && !editing && !accountsFor && (
        <ServicePanel
          service={selected}
          canManage={canManage}
          onClose={() => select(null)}
          onEdit={() => setEditing(selected)}
          onAccounts={() => setAccountsFor(selected)}
        />
      )}
      {editing && (
        <ServiceForm
          service={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
        />
      )}
      {accountsFor && (
        <AccountsPanel
          service={accountsFor}
          onClose={() => {
            setAccountsFor(null)
            load()
          }}
        />
      )}
    </>
  )
}

// ── Fiche d'un service (panneau latéral) ─────────────────────────────────────

function ServicePanel({
  service,
  canManage,
  onClose,
  onEdit,
  onAccounts,
}: {
  service: BettingService
  canManage: boolean
  onClose: () => void
  onEdit: () => void
  onAccounts: () => void
}) {
  const { t, fmt } = useI18n()
  const vocab = useBettingVocabulary()
  const [accounts, setAccounts] = useState<BettingTestAccount[] | null>(null)
  const [tests, setTests] = useState<PaymentTestRow[] | null>(null)
  const [launching, setLaunching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setAccounts(null)
    setTests(null)
    if (canManage) bettingApi.listAccounts(service.id).then(setAccounts).catch(() => setAccounts([]))
    bettingApi.listTests({ serviceId: service.id, limit: 5 }).then((r) => setTests(r.data)).catch(() => setTests([]))
  }, [service.id, canManage])

  const launch = async () => {
    setLaunching(true)
    setError(null)
    try {
      const test = await bettingApi.runJourney(service.id)
      navigate(`betting/tests/${test.id}`)
    } catch (err) {
      setError(errorMessage(err))
      setLaunching(false)
    }
  }

  const next = nextCheck(t, fmt, service)

  return (
    <SidePanel
      title={<>{service.name}<FixtureBadge when={service.isFixture} /></>}
      subtitle={[service.operatorCompany, countryLabel(vocab, service.country, t)].filter(Boolean).join(' · ')}
      onClose={onClose}
      footer={
        <>
          {canManage && <button type="button" className="btn btn-secondary" onClick={onEdit}>{t('common.edit')}</button>}
          <button type="button" className="btn btn-primary" disabled={!service.isActive || launching} onClick={launch}>
            {launching ? t('bet.launching') : t('bet.detectJourney')}
          </button>
        </>
      }
    >
      {error && <ErrorState message={error} compact />}
      {!service.isActive && <p className="bet-run-banner">{t('bet.serviceDisabled')}</p>}

      <section className="panel-section">
        <span className="panel-section-title">{t('bet.addresses')}</span>
        <div className="bet-address-list">
          <div><span className="bet-url">{service.mainUrl}</span><StatusPill>{t('bet.addressMain')}</StatusPill></div>
          {service.mirrorUrls.map((u) => (
            <div key={u}><span className="bet-url">{u}</span><StatusPill>{t('bet.addressMirror')}</StatusPill></div>
          ))}
        </div>
      </section>

      <section className="panel-section">
        <span className="panel-section-title">{t('bet.autoJourney')}</span>
        <dl className="field-grid">
          <div className="field"><dt>{t('bet.depthField')}</dt><dd>{depthLabel(t, service.journeyDepth)}</dd></div>
          <div className="field"><dt>{t('bet.colControl')}</dt><dd>{frequencyLabel(t, service.checkFrequencyDays)}</dd></div>
          <div className="field"><dt>{t('bet.depositPath')}</dt><dd>{service.journeyHints.length ? service.journeyHints.join(' › ') : t('bet.depositPathAuto')}</dd></div>
          <div className="field"><dt>{t('bet.nextCheck')}</dt><dd>{next ?? '—'}</dd></div>
          {service.journeyDepth === 'PAYMENT_REQUEST' && service.paymentRequestAmount ? (
            <div className="field"><dt>{t('bet.testAmount')}</dt><dd className="num">{service.paymentRequestAmount}</dd></div>
          ) : null}
        </dl>
      </section>

      {canManage && (
        <section className="panel-section">
          <div className="bet-panel-head">
            <span className="panel-section-title">{t('bet.testAccountsSection')}</span>
            <button type="button" className="block-link" onClick={onAccounts}>{t('bet.manageAccounts')}</button>
          </div>
          {accounts === null ? (
            <div className="stat-skeleton" style={{ minHeight: 40 }} />
          ) : accounts.length === 0 ? (
            <p className="empty-note">{t('bet.noAccountRobot')}</p>
          ) : (
            <ul className="row-list">
              {accounts.map((a) => (
                <li key={a.id} className="bet-account-row">
                  <span className="row-main">
                    <strong>{a.label}</strong>
                    <span>
                      {t('bet.accountUsername', { username: a.username })}
                      {a.testPhoneNumber ? t('bet.accountSim', { phone: a.testPhoneNumber }) : ''}
                    </span>
                  </span>
                  {!a.isActive ? (
                    <StatusPill>{t('bet.accountDisabled')}</StatusPill>
                  ) : a.lastUsedAt ? (
                    <span className="row-meta">{a.lastLoginResult ?? t('bet.accountUsedOn', { date: fmt.dateTime(a.lastUsedAt) })}</span>
                  ) : (
                    <StatusPill>{t('bet.accountNeverUsed')}</StatusPill>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="panel-section">
        <div className="bet-panel-head">
          <span className="panel-section-title">{t('bet.lastTests')}</span>
          <button type="button" className="block-link" onClick={() => navigate(withQuery('betting/tests', { serviceId: service.id }))}>
            {t('bet.allTestsLink')}
          </button>
        </div>
        {tests === null ? (
          <div className="stat-skeleton" style={{ minHeight: 60 }} />
        ) : tests.length === 0 ? (
          <p className="empty-note">{t('bet.neverTestedDot')}</p>
        ) : (
          <ul className="row-list">
            {tests.map((row) => (
              <li key={row.id}>
                <button type="button" className="row-link" onClick={() => navigate(`betting/tests/${row.id}`)}>
                  <span className="row-main">
                    <strong>
                      {row.runStatus === 'BLOCKED' ||
                      row.runStatus === 'GEO_BLOCKED' ||
                      row.runStatus === 'FAILED'
                        ? runStatusLabel(t, row.runStatus)
                        : severityLabel(t, row.severity)}
                    </strong>
                    <span>
                      {t('bet.testNumber', { n: row.testNumber })} ·{' '}
                      {row.source === 'AUTO' ? t('bet.source.autoLower') : t('bet.source.manualLower')}
                    </span>
                  </span>
                  <span className="row-meta num">{fmt.dateTime(row.createdAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {service.notes && (
        <section className="panel-section">
          <span className="panel-section-title">{t('common.notes')}</span>
          <p className="field-note">{service.notes}</p>
        </section>
      )}
    </SidePanel>
  )
}

// ── Formulaire service ───────────────────────────────────────────────────────

const lines = (s: string) => s.split('\n').map((l) => l.trim()).filter(Boolean)

function ServiceForm({ service, onClose, onSaved }: { service: BettingService | null; onClose: () => void; onSaved: () => void }) {
  const t = useT()
  const vocab = useBettingVocabulary()
  const [name, setName] = useState(service?.name ?? '')
  const [operatorCompany, setOperatorCompany] = useState(service?.operatorCompany ?? '')
  const [country, setCountry] = useState(service?.country ?? '')
  const [mainUrl, setMainUrl] = useState(service?.mainUrl ?? 'https://')
  const [mirrors, setMirrors] = useState((service?.mirrorUrls ?? []).join('\n'))
  const [hints, setHints] = useState((service?.journeyHints ?? []).join('\n'))
  const [depth, setDepth] = useState<JourneyDepth>(service?.journeyDepth ?? 'METHOD_SELECTION')
  const [amount, setAmount] = useState(service?.paymentRequestAmount ? String(service.paymentRequestAmount) : '')
  const [frequency, setFrequency] = useState(String(service?.checkFrequencyDays ?? 7))
  const [isActive, setIsActive] = useState(service?.isActive ?? true)
  const [notes, setNotes] = useState(service?.notes ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    setSaving(true)
    setError(null)
    const payload = {
      name: name.trim(),
      operatorCompany: operatorCompany.trim() || null,
      country,
      mainUrl: mainUrl.trim(),
      mirrorUrls: lines(mirrors),
      journeyHints: lines(hints),
      journeyDepth: depth,
      paymentRequestAmount: depth === 'PAYMENT_REQUEST' && amount ? Number(amount) : null,
      checkFrequencyDays: Number(frequency) || 0,
      isActive,
      notes: notes.trim() || null,
    }
    try {
      if (service) await bettingApi.updateService(service.id, payload)
      else await bettingApi.createService(payload)
      onSaved()
    } catch (err) {
      setError(errorMessage(err))
      setSaving(false)
    }
  }

  const invalid =
    name.trim().length < 2 || !country || !/^https?:\/\/.+\..+/.test(mainUrl.trim()) || (depth === 'PAYMENT_REQUEST' && !(Number(amount) > 0))

  return (
    <SidePanel
      wide
      title={service ? t('bet.serviceFormEdit', { name: service.name }) : t('bet.serviceFormNew')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="btn btn-primary" disabled={invalid || saving} onClick={submit}>
            {saving ? t('common.saving') : service ? t('common.save') : t('bet.addServiceSubmit')}
          </button>
        </>
      }
    >
      <div className="bet-form">
        <div className="bet-form-row">
          <label>
            <span>{t('bet.serviceName')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('bet.serviceNamePlaceholder')} />
          </label>
          <label>
            <span>{t('bet.operatorCompany')}</span>
            <input value={operatorCompany} onChange={(e) => setOperatorCompany(e.target.value)} placeholder={t('bet.operatorPlaceholder')} />
          </label>
          <label>
            <span>{t('common.country')}</span>
            <select value={country} onChange={(e) => setCountry(e.target.value)}>
              <option value="">{t('ads.pick')}</option>
              {vocab?.countries.map((c) => <option key={c.code} value={c.code}>{countryLabel(vocab, c.code, t)}</option>)}
            </select>
          </label>
        </div>
        <label>
          <span>{t('bet.mainAddress')}</span>
          <input value={mainUrl} onChange={(e) => setMainUrl(e.target.value)} placeholder="https://" inputMode="url" />
        </label>
        <label>
          <span>{t('bet.mirrorAddresses')}</span>
          <textarea rows={2} value={mirrors} onChange={(e) => setMirrors(e.target.value)} placeholder="https://1x-bet.mobi" />
          <small>{t('bet.mirrorHint')}</small>
        </label>
        <label>
          <span>{t('bet.hintsField')}</span>
          <textarea rows={2} value={hints} onChange={(e) => setHints(e.target.value)} placeholder={t('bet.hintsPlaceholder')} />
          <small>{t('bet.hintsHint')}</small>
        </label>

        <fieldset className="bet-depth">
          <legend>{t('bet.depthLegend')}</legend>
          <label className="bet-radio">
            <input type="radio" name="depth" checked={depth === 'METHOD_SELECTION'} onChange={() => setDepth('METHOD_SELECTION')} />
            <span>
              <strong>{t('bet.depth.METHOD_SELECTION')}</strong>
              <small>{t('bet.depthMethodHint')}</small>
            </span>
          </label>
          <label className="bet-radio">
            <input type="radio" name="depth" checked={depth === 'PAYMENT_REQUEST'} onChange={() => setDepth('PAYMENT_REQUEST')} />
            <span>
              <strong>{t('bet.depth.PAYMENT_REQUEST')}</strong>
              <small>{t('bet.depthRequestHint')}</small>
            </span>
          </label>
          {depth === 'PAYMENT_REQUEST' && (
            <div className="bet-depth-warning">
              <label>
                <span>{t('bet.testAmount')}</span>
                <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={t('bet.amountPlaceholderSimple')} />
              </label>
              <p>{t('bet.amountWarning')}</p>
            </div>
          )}
        </fieldset>

        <div className="bet-form-row">
          <label>
            <span>{t('bet.autoCheck')}</span>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              <option value="0">{t('bet.freqManualOnly')}</option>
              <option value="1">{t('bet.freqDaily')}</option>
              <option value="7">{t('bet.freqWeekly')}</option>
              <option value="14">{t('bet.freqFortnightly')}</option>
              <option value="30">{t('bet.freqMonthly')}</option>
            </select>
          </label>
          <label className="bet-check">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <span>{t('bet.serviceWatched')}</span>
          </label>
        </div>
        <label>
          <span>{t('common.notes')}</span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        {error && <ErrorState message={error} compact />}
      </div>
    </SidePanel>
  )
}

// ── Comptes de test ──────────────────────────────────────────────────────────

function AccountsPanel({ service, onClose }: { service: BettingService; onClose: () => void }) {
  const { t, fmt } = useI18n()
  const [accounts, setAccounts] = useState<BettingTestAccount[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({ label: '', username: '', password: '', loginUrl: '', testPhoneNumber: '' })
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    bettingApi.listAccounts(service.id).then(setAccounts).catch((err) => setError(errorMessage(err)))
  }, [service.id])
  useEffect(load, [load])

  const add = async () => {
    setSaving(true)
    setError(null)
    try {
      await bettingApi.createAccount(service.id, {
        label: form.label.trim(),
        username: form.username.trim(),
        password: form.password,
        loginUrl: form.loginUrl.trim() || null,
        testPhoneNumber: form.testPhoneNumber.trim() || null,
      })
      setForm({ label: '', username: '', password: '', loginUrl: '', testPhoneNumber: '' })
      load()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const toggle = async (a: BettingTestAccount) => {
    try {
      await bettingApi.updateAccount(a.id, { isActive: !a.isActive })
      load()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const remove = async (a: BettingTestAccount) => {
    if (!window.confirm(t('bet.deleteAccountConfirm', { label: a.label }))) return
    try {
      await bettingApi.deleteAccount(a.id)
      load()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  return (
    <SidePanel
      wide
      title={t('bet.accountsTitle', { name: service.name })}
      subtitle={t('bet.accountsSub')}
      onClose={onClose}
    >
      <div className="bet-form">
        {accounts === null ? (
          <div className="stat-skeleton" style={{ minHeight: 60 }} />
        ) : accounts.length === 0 ? (
          <p className="bet-empty">{t('bet.noAccount')}</p>
        ) : (
          <ul className="bet-account-list">
            {accounts.map((a) => (
              <li key={a.id} className={a.isActive ? '' : 'bet-row-inactive'}>
                <div>
                  <strong>{a.label}</strong>
                  <span>
                    {t('bet.accountUsername', { username: a.username })}
                    {a.testPhoneNumber ? t('bet.accountSim', { phone: a.testPhoneNumber }) : ''}
                  </span>
                  <span className="bet-muted">
                    {a.lastUsedAt
                      ? t('bet.accountUsedWith', { date: fmt.dateTime(a.lastUsedAt), result: a.lastLoginResult ?? '' })
                      : t('bet.accountNeverUsed')}
                  </span>
                </div>
                <div className="bet-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => toggle(a)}>{a.isActive ? t('users.deactivate') : t('ads.siteReactivate')}</button>
                  <button type="button" className="btn btn-danger" onClick={() => remove(a)}>{t('common.delete')}</button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <fieldset className="bet-account-form">
          <legend>{t('bet.declareAccount')}</legend>
          <div className="bet-form-row">
            <label>
              <span>{t('bet.accountLabel')}</span>
              <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={t('bet.accountLabelPlaceholder')} />
            </label>
            <label>
              <span>{t('bet.username')}</span>
              <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} autoComplete="off" />
            </label>
            <label>
              <span>{t('common.password')}</span>
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
            </label>
          </div>
          <div className="bet-form-row">
            <label>
              <span>{t('bet.loginPage')}</span>
              <input value={form.loginUrl} onChange={(e) => setForm({ ...form, loginUrl: e.target.value })} placeholder="https://" />
            </label>
            <label>
              <span>{t('bet.testSim')}</span>
              <input value={form.testPhoneNumber} onChange={(e) => setForm({ ...form, testPhoneNumber: e.target.value })} inputMode="tel" />
            </label>
          </div>
          <div className="bet-form-actions">
            <button type="button" className="btn btn-primary" disabled={saving || !form.label.trim() || !form.username.trim() || !form.password} onClick={add}>
              {saving ? t('common.saving') : t('bet.saveAccount')}
            </button>
          </div>
        </fieldset>
        {error && <ErrorState message={error} compact />}
      </div>
    </SidePanel>
  )
}
